import { useRef } from 'react'
import { motion, useMotionValue, useSpring } from 'motion/react'
import './TiltedCard.css'

const springValues = {
  damping: 30,
  stiffness: 100,
  mass: 2
}

/*
 * Adapted from react-bits' TiltedCard (https://reactbits.dev/components/tilted-card).
 * The original renders a fixed-size <img> with a cursor-following caption tooltip.
 * Ours wraps full-width/height card content (image + centered overlay) instead, since
 * our cards are section-width, not small image thumbnails — the tooltip/caption and
 * fixed pixel sizing don't apply, so those parts were dropped; the core mouse-tilt
 * spring physics are unchanged.
 */
export default function TiltedCard({
  imageSrc,
  altText = '',
  containerHeight = '360px',
  scaleOnHover = 1.03,
  rotateAmplitude = 10,
  overlayContent = null
}) {
  const ref = useRef(null)

  const rotateX = useSpring(useMotionValue(0), springValues)
  const rotateY = useSpring(useMotionValue(0), springValues)
  const scale = useSpring(1, springValues)

  function handleMouse(e) {
    if (!ref.current) return

    const rect = ref.current.getBoundingClientRect()
    const offsetX = e.clientX - rect.left - rect.width / 2
    const offsetY = e.clientY - rect.top - rect.height / 2

    rotateX.set((offsetY / (rect.height / 2)) * -rotateAmplitude)
    rotateY.set((offsetX / (rect.width / 2)) * rotateAmplitude)
  }

  function handleMouseEnter() {
    scale.set(scaleOnHover)
  }

  function handleMouseLeave() {
    scale.set(1)
    rotateX.set(0)
    rotateY.set(0)
  }

  return (
    <figure
      ref={ref}
      className="tilted-card-figure"
      style={{ height: containerHeight }}
      onMouseMove={handleMouse}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div className="tilted-card-inner" style={{ rotateX, rotateY, scale }}>
        <img src={imageSrc} alt={altText} className="tilted-card-img" />
        {overlayContent && <div className="tilted-card-overlay">{overlayContent}</div>}
      </motion.div>
    </figure>
  )
}
